import { useState, useEffect } from 'react';
import type { RuleExport } from '@mockd/shared/types/rule';
import { parseRulesImport } from '../../utils/exportEndpoint';
import { Alert } from '../common/Alert';
import { LoadingLabel } from '../common/Cursor';
import { Modal } from '../common/Modal';

interface ImportRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (rules: RuleExport[]) => Promise<void>;
}

export function ImportRulesModal({ isOpen, onClose, onImport }: ImportRulesModalProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<RuleExport[] | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setInput('');
      setError(null);
      setPreview(null);
    }
  }, [isOpen]);

  const handleParse = () => {
    setError(null);
    setPreview(null);
    try {
      setPreview(parseRulesImport(JSON.parse(input)));
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON. Please check your input.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to parse JSON');
      }
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      await onImport(preview);
      onClose();
    } catch (err) {
      // The hook has already toasted; keep the modal open with the reason.
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Rules"
      titleClassName="font-bold text-lg mb-4"
      boxClassName="max-w-2xl"
    >
      {error && (
        <Alert variant="error" className="mb-4">{error}</Alert>
      )}

      <div className="fieldset">
        <label className="fieldset-legend" htmlFor="import-rules-json">Paste exported rules JSON</label>
        <textarea
          id="import-rules-json"
          value={input}
          onChange={(e) => { setInput(e.target.value); setPreview(null); setError(null); }}
          placeholder={`{\n  "rules": [\n    { "name": "Not found", "priority": 0, "isActive": true }\n  ]\n}`}
          rows={8}
          className="textarea w-full font-mono text-xs"
          spellCheck={false}
          disabled={importing}
        />
      </div>
      {!preview && input.trim() && (
        <button type="button" onClick={handleParse} className="btn btn-sm btn-outline mt-2" disabled={importing}>
          Parse
        </button>
      )}

      {preview && (
        <div className="mt-4 border border-base-300 rounded-box p-4 bg-base-200">
          <h3 className="text-sm mb-2">
            {preview.length} rule{preview.length !== 1 ? 's' : ''} found
          </h3>
          <ul className="space-y-1 max-h-48 overflow-y-auto text-sm">
            {preview.map((rule, i) => (
              <li key={i} className="flex items-center gap-2">
                <span>{rule.name}</span>
                {!rule.isActive && <span className="badge badge-sm badge-ghost">inactive</span>}
              </li>
            ))}
          </ul>
          <p className="text-xs text-base-content/70 mt-3">
            Rules are added after the endpoint's existing rules.
          </p>
        </div>
      )}

      <div className="modal-action">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={importing}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleImport}
          disabled={!preview || preview.length === 0 || importing}
        >
          {importing ? (
            <LoadingLabel>Importing</LoadingLabel>
          ) : (
            <>Import{preview ? ` ${preview.length} rule${preview.length !== 1 ? 's' : ''}` : ''}</>
          )}
        </button>
      </div>
    </Modal>
  );
}
